import {
  Archive,
  Calendar,
  CheckCircle2,
  Clock,
  Crosshair,
  ExternalLink,
  History,
  LinkIcon,
  Pencil,
  Plus,
  Share2,
  Trash2,
  TrendingUp,
  Trophy,
  X,
} from "lucide-react";
import { useState } from "react";
import * as S from "../dashboard/_shared";
import Tag from "../tag";
import "./style.scss";

export default function AcaoDetalhe({
  a,
  onClose,
  onUpdate,
  onShare,
  onPublishWizard,
}) {
  const { C, SC, diasRest, fmtDt, pctChk, todayStr } = S;

  const [check, setCheck] = useState(a.check);
  const [status, setStatus] = useState(a.status);
  const [novaObs, setNovaObs] = useState("");
  const [hist, setHist] = useState(a.hist);
  // Edição inline de datas do checklist: valor = "<taskId>:<campo>" (campo = inicio | fim | concluido)
  const [editingDate, setEditingDate] = useState(null);
  function updateTaskDate(taskId, field, value) {
    const u = check.map((c) =>
      c.i === taskId ? { ...c, [field]: value || null } : c,
    );
    setCheck(u);
    onUpdate({ ...a, check: u });
    setEditingDate(null);
  }
  // ─── Sucesso (registrado quando a ação é concluída) ─────────────────────
  const [sucessoForm, setSucessoForm] = useState(false);
  const [ganhoInput, setGanhoInput] = useState("");
  const [obsInput, setObsInput] = useState("");
  // Abre o form de sucesso já pré-preenchendo o ganho com a evolução (atual - baseline)
  function abrirFormSucesso() {
    const delta = (Number(a.v_atual) || 0) - (Number(a.v_baseline) || 0);
    setGanhoInput(String(Math.max(0, Math.round(delta * 10) / 10)));
    setObsInput("");
    setSucessoForm(true);
  }
  function salvarSucesso(andPublish = false) {
    const ganho = parseFloat(ganhoInput);
    if (isNaN(ganho) || ganho < 0) return;
    const novoSucesso = {
      ganhoPp: ganho,
      observacao: obsInput.trim(),
      registradoEm: todayStr(),
    };
    const acaoComSucesso = { ...a, sucesso: novoSucesso };
    onUpdate(acaoComSucesso);
    setSucessoForm(false);
    setGanhoInput("");
    setObsInput("");
    // Se o usuário escolheu "Registrar + Publicar", abre o wizard de publicação
    if (andPublish && onPublishWizard) {
      onPublishWizard(acaoComSucesso);
    }
  }
  function removerSucesso() {
    if (!confirm("Remover o sucesso registrado desta ação?")) return;
    onUpdate({ ...a, sucesso: null });
  }
  const sc = SC[status] || SC.pendente;
  const pr = pctChk(check);
  function tog(id) {
    const today = todayStr();
    const u = check.map((c) =>
      c.i === id ? { ...c, f: !c.f, concluido: !c.f ? today : null } : c,
    );
    setCheck(u);
    onUpdate({ ...a, check: u });
  }
  function addObs() {
    if (!novaObs.trim()) return;
    const u = [...hist, { dt: todayStr(), tx: novaObs.trim() }];
    setHist(u);
    onUpdate({ ...a, hist: u });
    setNovaObs("");
  }
  function chgSt(s) {
    setStatus(s);
    onUpdate({ ...a, status: s });
  }
  // ─── Adicionar links inline direto no AcaoDetalhe ──────────────────────
  const [novoLinkLabel, setNovoLinkLabel] = useState("");
  const [novoLinkUrl, setNovoLinkUrl] = useState("");
  const [linkFormOpen, setLinkFormOpen] = useState(false);
  function addLinkInline() {
    if (!novoLinkUrl.trim()) return;
    const url = novoLinkUrl.trim();
    const label =
      novoLinkLabel.trim() || url.replace(/^https?:\/\//, "").slice(0, 40);
    const novoLink = { id: Date.now(), label, url };
    const listaAtual = a.links || [];
    onUpdate({ ...a, links: [...listaAtual, novoLink] });
    setNovoLinkLabel("");
    setNovoLinkUrl("");
    setLinkFormOpen(false);
  }
  function removeLinkInline(linkId) {
    onUpdate({ ...a, links: (a.links || []).filter((l) => l.id !== linkId) });
  }
  const isCes = a.tipo === "ces";
  return (
    <div className="compAcaoDetalhe">
      <div className="header">
        <div className="header-left">
          <div className="tag-row">
            <Tag label={a.etapa} color={isCes ? C.blue : C.g700} />
            <Tag
              label={isCes ? "CES" : "CSAT"}
              color={isCes ? C.blue : C.g700}
            />
            {a.origem && <Tag label={a.origem} color={C.p500} />}
          </div>
          <h3 className="title">{a.titulo}</h3>
          <p className="desc">{a.desc}</p>
          {/* Counter de dias em aberto (desde criação até hoje ou até conclusão) */}
          {(() => {
            const histOrdenado = (a.hist || [])
              .filter((h) => h.dt)
              .map((h) => h.dt)
              .sort();
            const dtCriacao = histOrdenado[0] || null;
            if (!dtCriacao) return null;
            const dtFim =
              a.status === "concluido" && a.sucesso?.registradoEm
                ? a.sucesso.registradoEm
                : todayStr();
            const diasAbertos = Math.max(
              0,
              Math.floor(
                (new Date(dtFim) - new Date(dtCriacao)) / (1000 * 60 * 60 * 24),
              ),
            );
            const isConcluido = a.status === "concluido";
            const iconBg = isConcluido
              ? C.greenBg
              : diasAbertos > 90
                ? C.orangeBg || "#FFFBEB"
                : C.blueBg;
            const iconColor = isConcluido
              ? C.green
              : diasAbertos > 90
                ? C.orange || "#d97706"
                : C.blue;
            const label = isConcluido
              ? `Plano ativo por ${diasAbertos} dia${diasAbertos === 1 ? "" : "s"} (criado em ${fmtDt(dtCriacao)})`
              : `${diasAbertos} dia${diasAbertos === 1 ? "" : "s"} em aberto desde a criação (${fmtDt(dtCriacao)})`;
            return (
              <div
                className="dias-badge"
                style={{
                  "--_dias-color": iconColor,
                  "--_dias-bg": iconBg,
                  "--_dias-border": `${iconColor}30`,
                }}
              >
                <Clock size={10} /> {label}
              </div>
            );
          })()}
        </div>
        <button className="close-btn" onClick={onClose}>
          <X size={20} />
        </button>
      </div>

      {/* Banner ARQUIVADA — visível só quando status === arquivado */}
      {status === "arquivado" && (
        <div className="archived-banner">
          <Archive size={16} color="#6B7280" />
          <div className="archived-text">
            <div className="archived-title">Ação arquivada</div>
            <div className="archived-sub">
              Esta ação não aparece no filtro padrão. Selecione o status
              "Arquivado" no filtro pra encontrá-la.
            </div>
          </div>
          <button
            onClick={() => chgSt("concluido")}
            className="unarchive-btn"
          >
            <Archive size={12} /> Desarquivar
          </button>
        </div>
      )}

      {/* Bloco SUCESSO — só aparece quando ação está concluída */}
      {status === "concluido" && (
        <div className="sucesso-wrap">
          {a.sucesso ? (
            // Sucesso registrado: card destaque
            <div className="sucesso-card">
              <div className="sucesso-icon">
                <Trophy size={22} color="#FFFFFF" />
              </div>
              <div className="sucesso-body">
                <div className="sucesso-label">Sucesso registrado</div>
                <div className="sucesso-ganho">
                  +{a.sucesso.ganhoPp} pp em {a.etapa}
                </div>
                {a.sucesso.observacao && (
                  <div className="sucesso-obs">{a.sucesso.observacao}</div>
                )}
                <div className="sucesso-date">
                  Registrado em {fmtDt(a.sucesso.registradoEm)}
                </div>
              </div>
              <div className="sucesso-actions">
                <button
                  onClick={() =>
                    onPublishWizard ? onPublishWizard(a) : onShare && onShare(a)
                  }
                  title="Publicar este plano no CooperAção Unimed (outras Unimeds poderão ver e reusar)"
                  className="publish-btn"
                >
                  <Share2 size={12} /> Publicar CooperAção
                </button>
                <button
                  onClick={removerSucesso}
                  title="Remover sucesso registrado"
                  className="remove-sucesso-btn"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          ) : sucessoForm ? (
            // Form aberto
            <div className="sucesso-form">
              <div className="sucesso-form-header">
                <Trophy size={16} color={C.green} />
                <div className="sucesso-form-title">
                  Registrar sucesso desta ação
                </div>
              </div>
              <div className="sucesso-form-fields">
                <div className="sucesso-form-field">
                  <label className="form-label">Quanto melhorou (pp)</label>
                  <input
                    type="number"
                    autoFocus
                    value={ganhoInput}
                    onChange={(e) => setGanhoInput(e.target.value)}
                    placeholder="ex: 5"
                    min="0"
                    step="0.5"
                    className="form-input input-ganho"
                  />
                </div>
                <div className="sucesso-form-field wide">
                  <label className="form-label">Observação (opcional)</label>
                  <input
                    value={obsInput}
                    onChange={(e) => setObsInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && salvarSucesso()}
                    placeholder="Como sabe que funcionou? Qual foi o resultado?"
                    className="form-input"
                  />
                </div>
              </div>
              <div className="sucesso-form-actions">
                <button
                  onClick={() => {
                    setSucessoForm(false);
                    setGanhoInput("");
                    setObsInput("");
                  }}
                  className="btn-cancel"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => salvarSucesso(false)}
                  disabled={!ganhoInput || isNaN(parseFloat(ganhoInput))}
                  className="btn-registrar"
                >
                  <Trophy size={12} /> Registrar sucesso
                </button>
                <button
                  onClick={() => salvarSucesso(true)}
                  disabled={!ganhoInput || isNaN(parseFloat(ganhoInput))}
                  title="Registrar sucesso e abrir publicação no CooperAção Unimed"
                  className="btn-registrar-publish"
                >
                  <Share2 size={12} /> Registrar + Publicar CooperAção
                </button>
              </div>
            </div>
          ) : (
            // Botão pra abrir o form
            <button onClick={abrirFormSucesso} className="btn-registrar-sucesso">
              <Trophy size={16} /> Registrar sucesso desta ação
            </button>
          )}
        </div>
      )}

      <div className="body-grid">
        {/* Left column */}
        <div className="left-col">
          {/* Evolução desde a criação do plano (baseline → atual) */}
          {a.v_baseline !== undefined &&
            a.v_baseline !== null &&
            (() => {
              const evolucao = Number(a.v_atual) - Number(a.v_baseline);
              const isPos = evolucao > 0;
              const isNeg = evolucao < 0;
              const cor = isPos ? C.green : isNeg ? C.red : C.text4;
              const bg = isPos
                ? C.greenBg
                : isNeg
                  ? C.redBg || "#FEE2E2"
                  : C.surf3;
              const unidade = a.etapa === "NPS Geral" ? "pts" : "pp";
              return (
                <div
                  className="evolucao-card"
                  style={{
                    "--_evo-bg": bg,
                    "--_evo-border": `${cor}30`,
                    "--_evo-color": cor,
                  }}
                >
                  <div className="evolucao-header">
                    <TrendingUp size={10} /> Evolução desde a criação do plano
                  </div>
                  <div className="evolucao-row">
                    <div className="evolucao-val">
                      <b className="evolucao-val-b baseline">
                        {Math.round(a.v_baseline)}
                        {a.etapa === "NPS Geral" ? "" : "%"}
                      </b>{" "}
                      <span className="evolucao-val-hint">ao criar</span>
                    </div>
                    <div className="evolucao-arrow">→</div>
                    <div className="evolucao-val">
                      <b className="evolucao-val-b current">
                        {Math.round(a.v_atual)}
                        {a.etapa === "NPS Geral" ? "" : "%"}
                      </b>{" "}
                      <span className="evolucao-val-hint">agora</span>
                    </div>
                    <div className="evolucao-delta">
                      {isPos ? "+" : ""}
                      {Math.round(evolucao)} {unidade}
                    </div>
                  </div>
                </div>
              );
            })()}
          {/* KPIs */}
          <div className="kpi-grid">
            {[
              [
                "Atual",
                `${Math.round(a.v_atual)}%`,
                (SC[a.status] && SC[a.status].color) || C.text,
              ],
              ["Meta", `${Math.round(a.v_meta)}%`, C.p500],
              [
                "Gap",
                `${Math.round(Math.abs(a.v_meta - a.v_atual))}pp`,
                a.v_atual >= a.v_meta ? C.green : C.orange,
              ],
              [
                "Prazo",
                fmtDt(a.prazo),
                diasRest(a.prazo) < 0 ? C.red : C.text3,
              ],
            ].map(([l, v, c]) => (
              <div key={l} className="kpi-card">
                <div className="kpi-label">{l}</div>
                <div
                  className="kpi-value"
                  style={{ "--_kpi-color": c }}
                >
                  {v}
                </div>
              </div>
            ))}
          </div>
          {/* Status */}
          <div>
            <div className="section-label">Status</div>
            <div className="status-pills">
              {Object.entries(SC)
                .filter(
                  ([k]) =>
                    k !== "arquivado" ||
                    status === "concluido" ||
                    status === "arquivado",
                )
                .map(([k, v]) => (
                  <button
                    key={k}
                    onClick={() => chgSt(k)}
                    className={`status-pill${status === k ? " is-active" : ""}`}
                    style={status === k ? { "--_st-color": v.color } : undefined}
                  >
                    {v.label}
                  </button>
                ))}
            </div>
          </div>
          {/* Sparkline */}
          {a.med && a.med.length > 1 && (
            <div className="sparkline-card">
              <div className="sparkline-label">Evolução</div>
              <div className="sparkline-row">
                {a.med.map((m, i) => (
                  <div key={i} className="sparkline-item">
                    <div className="sparkline-val">{Math.round(m.v)}%</div>
                    <div className="sparkline-period">{m.p}</div>
                  </div>
                ))}
                <div className="sparkline-item">
                  <div className="sparkline-meta-val">{Math.round(a.v_meta)}%</div>
                  <div className="sparkline-meta-label">Meta</div>
                </div>
              </div>
            </div>
          )}
          {/* History */}
          <div>
            <div className="section-label with-icon">
              <History size={10} /> Histórico de atualizações
            </div>
            <div className="history-list">
              {hist.map((h, i) => (
                <div key={i} className="history-item">
                  <div className="history-dot" />
                  <div className="history-body">
                    <div className="history-meta">
                      <div className="history-date">
                        {fmtDt(h.dt)}
                        {h.hora ? ` · ${h.hora}` : ""}
                      </div>
                      {h.user && (
                        <div className="history-user">{h.user}</div>
                      )}
                    </div>
                    <div className="history-text">{h.tx}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="obs-row">
              <input
                value={novaObs}
                onChange={(e) => setNovaObs(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addObs()}
                placeholder="Adicionar observação..."
                className="obs-input"
              />
              <button onClick={addObs} className="obs-btn">
                <Plus size={13} />
              </button>
            </div>
          </div>
          {/* Drivers afetados (read-only) */}
          {a.drivers && a.drivers.length > 0 && (
            <div>
              <div className="section-label with-icon">
                <Crosshair size={10} /> Drivers afetados
              </div>
              <div className="drivers-list">
                {a.drivers.map((slug) => {
                  const drv = null;
                  if (!drv) return null;
                  return (
                    <div key={slug} title={drv.desc} className="driver-tag">
                      <span className="driver-icon">{drv.icon}</span>
                      {drv.label}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Links e arquivos (Drive, Dropbox, etc) — sempre visível, com input inline */}
          <div>
            <div className="links-header">
              <span className="links-header-left">
                <LinkIcon size={10} /> Links e arquivos{" "}
                {(a.links || []).length > 0 && (
                  <span className="links-count">
                    · {(a.links || []).length}
                  </span>
                )}
              </span>
              {!linkFormOpen && (
                <button
                  onClick={() => setLinkFormOpen(true)}
                  className="add-link-btn"
                >
                  <Plus size={10} /> Adicionar
                </button>
              )}
            </div>
            {/* Lista de links existentes */}
            {(a.links || []).length === 0 && !linkFormOpen && (
              <div className="no-links">
                Nenhum link adicionado. Clique em <b>+ Adicionar</b> para anexar
                documentos, especificações ou relatórios.
              </div>
            )}
            {(a.links || []).length > 0 && (
              <div className={`links-list${linkFormOpen ? " mb-8" : ""}`}>
                {(a.links || []).map((l) => (
                  <div key={l.id} className="link-item">
                    <LinkIcon
                      size={12}
                      color={C.blue}
                      className="link-icon"
                    />
                    <a
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link-anchor"
                    >
                      <div className="link-label">{l.label}</div>
                      <div className="link-url">{l.url}</div>
                    </a>
                    <a
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Abrir em nova aba"
                      className="link-open"
                    >
                      <ExternalLink size={11} color={C.blue} />
                    </a>
                    <button
                      onClick={() => removeLinkInline(l.id)}
                      title="Remover link"
                      className="link-remove-btn"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {/* Form inline de adicionar link */}
            {linkFormOpen && (
              <div className="link-form">
                <input
                  autoFocus
                  value={novoLinkLabel}
                  onChange={(e) => setNovoLinkLabel(e.target.value)}
                  placeholder="Nome (opcional — ex: Especificação técnica)"
                  className="link-form-input"
                />
                <input
                  value={novoLinkUrl}
                  onChange={(e) => setNovoLinkUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addLinkInline()}
                  placeholder="https://drive.google.com/..."
                  className="link-form-input"
                />
                <div className="link-form-actions">
                  <button
                    onClick={() => {
                      setLinkFormOpen(false);
                      setNovoLinkLabel("");
                      setNovoLinkUrl("");
                    }}
                    className="link-form-cancel"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={addLinkInline}
                    disabled={!novoLinkUrl.trim()}
                    className="link-form-add"
                  >
                    <Plus size={10} /> Adicionar link
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Right column — checklist */}
        <div className="right-col">
          <div className="checklist-header">
            <div className="checklist-label">Checklist — {pr}%</div>
            <span className="checklist-count">
              {check.filter((c) => c.f).length}/{check.length}
            </span>
          </div>
          <div className="checklist-track">
            <div
              className="checklist-fill"
              style={{ "--_pr-pct": `${pr}%` }}
            />
          </div>
          <div className="checklist-items">
            {check.map((c) => (
              <div
                key={c.i}
                onClick={() => tog(c.i)}
                className={`checklist-item${c.f ? " is-done" : ""}`}
              >
                <div className={`checklist-checkbox${c.f ? " is-done" : ""}`}>
                  {c.f && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path
                        d="M1 4L3.5 6.5L9 1"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <div className="checklist-body">
                  <div className={`checklist-text${c.f ? " is-done" : ""}`}>
                    {c.t}
                  </div>
                  <div
                    className="checklist-dates"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Início */}
                    {editingDate === `${c.i}:inicio` ? (
                      <input
                        type="date"
                        autoFocus
                        value={c.inicio || ""}
                        onChange={(e) =>
                          updateTaskDate(c.i, "inicio", e.target.value)
                        }
                        onBlur={() => setEditingDate(null)}
                        className="date-inline-input"
                      />
                    ) : (
                      <span className="date-span">
                        <Calendar size={9} />
                        Início: {c.inicio ? fmtDt(c.inicio) : "—"}
                        <Pencil
                          size={9}
                          color={C.text4}
                          style={{
                            cursor: "pointer",
                            opacity: 0.6,
                            marginLeft: 2,
                          }}
                          onClick={() => setEditingDate(`${c.i}:inicio`)}
                        />
                      </span>
                    )}
                    {/* Fim */}
                    {editingDate === `${c.i}:fim` ? (
                      <input
                        type="date"
                        autoFocus
                        value={c.fim || ""}
                        onChange={(e) =>
                          updateTaskDate(c.i, "fim", e.target.value)
                        }
                        onBlur={() => setEditingDate(null)}
                        className="date-inline-input"
                      />
                    ) : (
                      <span className="date-span">
                        <Clock size={9} />
                        Fim: {c.fim ? fmtDt(c.fim) : "—"}
                        <Pencil
                          size={9}
                          color={C.text4}
                          style={{
                            cursor: "pointer",
                            opacity: 0.6,
                            marginLeft: 2,
                          }}
                          onClick={() => setEditingDate(`${c.i}:fim`)}
                        />
                      </span>
                    )}
                    {/* Concluído (só aparece se tarefa está marcada) */}
                    {c.f &&
                      (editingDate === `${c.i}:concluido` ? (
                        <input
                          type="date"
                          autoFocus
                          value={c.concluido || ""}
                          onChange={(e) =>
                            updateTaskDate(c.i, "concluido", e.target.value)
                          }
                          onBlur={() => setEditingDate(null)}
                          className="date-inline-input green"
                        />
                      ) : (
                        <span className="date-span green">
                          <CheckCircle2 size={9} />
                          Concluído:{" "}
                          {c.concluido ? fmtDt(c.concluido) : todayStr()}
                          <Pencil
                            size={9}
                            color={C.green}
                            style={{
                              cursor: "pointer",
                              opacity: 0.7,
                              marginLeft: 2,
                            }}
                            onClick={() => setEditingDate(`${c.i}:concluido`)}
                          />
                        </span>
                      ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
